import type {
	contentImage,
	contentOEmbedCode,
	CommentFilter,
	Newsletter,
	Page,
	Post,
	presenterShortCollection,
	User,
	Hub,
	SocialSsoProvider,
	sparq3FilterPayload,
} from '$lib/api/rpc';
import type { Collection, CustomCollection } from '$lib/types/Collections';

/* eslint-disable */
export interface Request {
	method: string;
	path: string;
	headers?: any;
	data?: any;
}

/**
 * @template T - Type of the data property, defaults to any
 * @property {any} [error] - Error / handled exception, i.e., when client fails to fetch the data
 * @property {number} status - HTTP response code
 * @property {string} vctraceid - Trace ID for debugging
 * @property {T} [data] - Response data payload
 * @property {Headers} [headers] - HTTP response headers
 */
type Response<TData = any> = {
	error?: any;
	status: number;
	vctraceid: string;
	data?: TData;
	headers?: Headers;
};

interface UpdatePasswordRequest {
	session_id: string;
	org_id?: string;
	hub_id?: string;
	current_password?: string;
	password: string;
	password_confirmation: string;
}

interface UploadImageResponse {
	id: string;
	url: string;
	error?: any;
}

type Image = {
	url: string;
	height: number;
	width: number;
	format: string;
};

export type UploadFileDataResponse = {
	asset: Post;
	content_type: string;
	created_at: string;
	description: string;
	download_url: string;
	embed_url: string;
	ext: string;
	filename: string;
	filesize: number;
	hash: string;
	hub_id: string | number | null;
	id: string;
	image: Image;
	metadata: any;
	org_id: string | number | null;
	public: boolean;
	user_id: string | number | null;
};

export interface UploadFileResponse {
	data?: UploadFileDataResponse;
	status: number;
	vctraceid: string;
	error?: any;
}

export interface SignOutResponse {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: { redirect_url: string };
}

interface NotificationResponse {
	data?: Notification[];
	status: number;
	vctraceid: string;
	error?: string;
	headers?: Headers;
}

export type SamlProvider = 'onelogin' | 'azure';
export type SamlConfig = {
	id: string;
	uid: string;
	entry_point_url: string;
	org_id: string;
	org_role?: string;
	hubs_ids?: string;
	default_user_role?: string;
	updated_at?: string;
	last_login_at?: string;
	require_email_confirmation: boolean;
	trusted_domains: Array<string>;
	metadata_url: string;
	metadata_xml: string;
	cert: string;
	cert_created_at?: string;
	cert_expires_at?: string;
	issuer_url: string;
	sso_service_url?: string;
	sso_service_binding: string;
	sign_saml_request: boolean;
	provider_user_id_key?: string;
	username_key?: string;
	email_key?: string;
	fullname_key?: string;
	firstname_key?: string;
	lastname_key?: string;
	allow_self_signed_cert: boolean;
	trust_unknown_authority: boolean;
	hub_id?: string;
	enabled: boolean;
	type: SamlProvider;
	button_text?: string;
	acs_url?: string;
	idp_login_url?: string;
};
export type SamlError = {
	id: string;
	error_message: string;
	created_at: string;
};

export interface Notification {
	id: string;
	user_id: string;
	post_id?: string | null;
	hub_id: string;
	type: string;
	etc: Etc;
	read_at?: string | null;
	created_at: string;
}
export interface Etc {
	user: User;
	post?: NotificationPost;
	comment?: Comment;
	hub?: {
		title: string;
		url: string;
	};
	invite?: Invite;
}
export interface NotificationPost {
	title: string;
	slug: string;
	short_url: string;
	url: string;
}
export interface Comment {
	title: string;
	slug: string;
	short_url: string;
	body: string;
}
export interface Invite {
	hash: string;
	status: string;
	role: string;
}
export interface notificationSettingResponse {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: { status: string };
}

export interface InviteByEmailData {
	invitee_role: string;
	message: string;
	invitee_email: string;
}

export interface LookupUserResponse {
	error?: any;
	status: number;
	vctraceid: string;
	data?: { action: string };
}

export interface UserMentionsResponse {
	error?: any;
	status: number;
	vctraceid: string;
	data?: Array<{ id: string; type: string; name: string; avatar_url: string }>;
}

export interface updatePasswordResponse {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: { code: string; error?: string };
}

export interface FetchPerformancePagePostsResponse {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: HubMetricsInterface[];
}

export type PostsResponse = Response<Post[]>;

export interface HubMetricsInterface {
	type: string;
	title: string;
	status: string;
	created_at: string;
	published_at: string;
	user: {
		id: string;
		name: string;
	};
	total_views?: number;
	like_count?: number;
	comment_count?: number;
	id: string;
}

type CollectionsResponse = {
	data?: CustomCollection[];
	status: number;
	vctraceid: string;
	error?: string;
	headers?: Headers;
};

type CollectionResponse = {
	data?: CustomCollection;
	status: number;
	vctraceid: string;
	error?: string;
	headers?: Headers;
};

type HubResponse = {
	data?: Hub;
	status: number;
	vctraceid: string;
	error?: string;
	headers?: Headers;
};

export interface FetchPostContributions {
	error?: any;
	status: number;
	vctraceid: string;
	data?: Post[];
}

export interface Tag {
	name: string;
	count: number;
}
export interface FetchTagsResponse {
	error?: any;
	status: number;
	vctraceid: string;
	data?: Tag[];
}

export interface FetchPostManage {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: Array<Post>;
}

export interface FetchPerformancePageEnagegmentResponse {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: PerformanceEnagegmentInterface;
}

export interface PerformanceEnagegmentInterface {
	posts: {
		unique: number;
		engagement: number;
		share: number;
	};
	users: {
		unique: number;
		engagement: number;
		share: number;
	};
	total_views: number;
	total_shares: number;
	user_views: number;
	anonymous_views: number;
	user_shares: number;
	anonymous_shares: number;
	orgadmin_views: number;
	orgadmin_shares: number;
	like_count: number;
	comment_count: number;
	anonymous_network_shares: {
		email: number;
		facebook: number;
		twitter: number;
		linkedin: number;
		unknown: number;
	};
	network_shares: {
		email: number;
		facebook: number;
		twitter: number;
		linkedin: number;
		unknown: number;
	};
}

export interface FetchPerformancePageUsersType {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: UserMetrics[];
}
export interface FetchPerformancePageUserResponse {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: UserMetrics[];
}

export interface UserMetrics {
	id: string;
	start: string;
	end: string;
	duration: number;
	events: {
		ts: string;
		role: string;
		event_type: string;
	}[];
}

export interface AddonsResponse {
	error?: any; // Error / handled exception, ie. when client fails to fetch the data.
	status: number; // HTTP response code.
	vctraceid: string;
	data?: AddonsResponseData[];
}

export interface AddonsResponseData {
	name: string;
	type: string;
	configs: ConfigsEntity[] | null;
}
export interface ConfigsEntity {
	id: string;
	label: string;
	hub_id: string;
	is_enabled: boolean;
	values: AddonValues;
	addon_type: string;
}

export interface AddonValues {
	javascript?: string;
	css?: string;
	content?: string;
	attribute?: string;
	attribute_value?: string;
	url?: string;
	size?: number;
	html?: string;
	extension_point?: string;
	tracking_id?: string;
	tracker_version?: string;
	app_id?: string;
	team_id?: string;
}

interface NewsletterResponse {
	data?: Newsletter;
	status: number;
	vctraceid: string;
	error?: string;
}

export interface NewsletterPreviewRequest {
	post: Post;
}

type CreateCollectionRequest = Pick<CustomCollection, 'name' | 'slug' | 'tag_counts' | 'tags'>;
export interface HTML {
	html: string;
}
export interface NewsletterPreviewResponse {
	status: number;
	vctraceid: string;
	error?: string;
	data?: HTML;
}

export interface NewsletterRecipient {
	clicks: Array<any>;
	email: string;
	name: string;
	id: string;
	user_id: string;
	newsletter_id: string;
	remote_ip: string;
	status: 'delivered' | 'pending' | 'scheduled' | 'rejected' | 'bounced';
	metrics: {
		delivered: boolean;
		opened: boolean;
		unsubscribed: boolean;
		clicked: boolean;
		clicked_posts: Array<string>;
		shared: boolean;
		shared_posts: Array<Object>;
	};
	status_reason?: string;
	created_at?: string;
	bounced_at?: string;
	rejected_at?: string;
	delivered_at?: string;
	unsubscribed_at?: string;
	first_clicked_at?: string;
	first_opened_at?: string;
	marked_spam_at?: string;
}

export interface NewsletterRecipientsResponse {
	status: number;
	vctraceid: string;
	error?: string;
	data?: NewsletterRecipient[];
}

export interface ActivitiesResponse {
	status: number;
	vctraceid: string;
	error?: string;
	data?: Activity[];
}

//TODO: This interface may not be complete
export interface Activity {
	id: string;
	name: string;
	url: string;
	opened_on: Date;
	status: string;
	type: string;
	hidden: boolean;
	created_at: Date;
	updated_at: Date;
	participation: ActivityParticipation;
}

export interface ActivityParticipation {
	member_id: string;
	communication_id: string;
	communication_status: string;
	communication_type: string;
}

export type PostStatus =
	| 'draft'
	| 'published'
	| 'hidden'
	| 'scheduled'
	| 'submission'
	| 'in-review'
	| 'deleted'
	| undefined;

export const postTypesList = [
	'audio',
	'document',
	'file',
	'link',
	'photo',
	'text',
	'video',
	'post',
	'repost',
	'contribution',
	'gallery',
	'question',
	'newsletter',
	'widget',
	'widgetQuickPoll',
	'widgetLink',
	'widgetHtml',
] as const;
export type PostTypes = (typeof postTypesList)[number];

// TODO: This interface is incomplete
export interface PostChild {
	type: PostTypes;
	body: string;
	children?: PostChild[];
	etc?: any;
	description?: string;
}

export interface PostParent {
	id: string | null;
	status: PostStatus;
	type: PostTypes;
	body?: string;
	children: PostChild[];
	title_image: contentImage;
	collection_id: string | undefined;
	collection: Collection | presenterShortCollection | undefined;
	tags: string[];
	context: string | undefined;
	title: string;
	hide_all: false;
	oembed?: contentOEmbedCode;
	description?: string;
	published_at?: string;
	root_id?: string;
	etc?: {
		widget?: {
			html?: string;
			url?: string;
		};
	};
}

export type PVChangeFrequency = 'unlimited' | 'annually' | 'every_six_months';
type PVType = 'singleChoice' | 'multiChoice' | 'open';
type PVDataType = 'text' | 'numeric' | 'date';

export interface PV {
	id: string;
	sparq_id: string;
	hub_id: string;
	category: PVCategory | null;
	visible: boolean;
	member_facing_name: string;
	profile_variable_name: string;
	order: number;
	change_frequency: PVChangeFrequency;
	member_can_update: boolean;
	sparq_read_only: boolean;
	read_only: boolean;
	profile_variable_type: PVType;
	member_values: null;
	profile_variable_data_type: PVDataType;
	visible_to_members: boolean;
	visible_to_moderators: boolean;
	profile_variable_values: null;
}

export interface PVCategory {
	id: string;
	hub_id: string;
	name: string;
	count?: number;
	created_at: Date;
	updated_at: Date;
}

export type RestApiClientType = InstanceType<typeof RestApiClient>;

export type AIConsent = {
	has_consent: boolean;
};

// Hubs REST API client.
// Handles exceptions. All methods return well-defined type including status and error field.
export default class RestApiClient {
	apiUrl: string;
	jwt: string;
	debug: boolean;
	csrfToken: string;

	constructor(apiUrl: string, jwt: string, debug: boolean, csrfToken: string) {
		this.apiUrl = apiUrl;
		this.jwt = jwt;
		this.debug = debug;
		this.csrfToken = csrfToken;
	}

	sendRequest = async (request: Request): Promise<Response> => {
		const reqUrl = `${this.apiUrl}${request.path}`;
		const headers = {
			Accept: `application/json`,
			Authorization: `Bearer ${this.jwt}`,
			['x-csrf-token']: this.csrfToken,
			...request.headers,
		};

		const response: Response = {
			status: 420, // Default response status (420 "Enhance Your Calm") just in case we can't send request at all.
			vctraceid: '',
		};

		let resp;
		try {
			const reqInit: RequestInit = {
				headers,
				method: request.method,
			};
			if (request.data) {
				if (Object.prototype.toString.call(request.data) === '[object FormData]') {
					reqInit.body = request.data;
				} else {
					reqInit.body = JSON.stringify(request.data);
					reqInit.headers = { ...reqInit.headers, 'Content-Type': 'application/json' };
				}
			}
			resp = await fetch(reqUrl, reqInit);

			response.status = resp.status;
			response.headers = resp.headers;
			if (resp.headers.get('x-vctraceid')) {
				response.vctraceid = resp.headers.get('x-vctraceid')!;
			}
		} catch (e) {
			response.error = e;
			if (this.debug) {
				console.log(`>>> RestApiClient failed to fetch ${reqUrl}: ERROR: ${e}`, response);
			}
			return response;
		}

		if (resp.status !== 204) {
			// Get data from the response body.
			try {
				if (resp.headers.get('content-type')?.includes('application/json')) {
					response.data = await resp.json();
				}
				if (resp.headers.get('content-type')?.includes('text/csv')) {
					response.data = await resp.text();
				}
			} catch (e) {
				response.error = e;
				console.error(`failed to parse data: ${resp}`);
			}
		}

		if (response.status >= 400) {
			response.error = `HTTP ${response.status}`;
		}

		if (this.debug) {
			console.debug(`>>> RestApiClient ${reqUrl}: HTTP ${response.status}`);
		}

		return response;
	};

	uploadUserImage = async (sessionId: string, file: Blob): Promise<UploadImageResponse> => {
		const form = new FormData();
		form.append('attachment', file);
		const { data, error } = await this.sendRequest({
			method: 'POST',
			path: `/users/${sessionId}/files`,
			data: form,
		});

		return {
			id: data.id,
			url: data.download_url,
			error: error,
		};
	};

	uploadFile = async (hubId: string, file: Blob): Promise<UploadFileResponse> => {
		const form = new FormData();
		form.append('attachment', file);
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/files`,
			data: form,
		});
	};

	leaveCommunity = async (hubId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/hubs/${hubId}/users/me`,
		});
	};

	revokeInvite = async (hash: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/invites/${hash}`,
		});
	};

	updatePassword = async ({
		session_id,
		org_id,
		hub_id,
		current_password,
		password,
		password_confirmation,
	}: UpdatePasswordRequest): Promise<updatePasswordResponse> => {
		return await this.sendRequest({
			method: 'PATCH',
			path: `/users/${session_id}/password`,
			data: {
				org_id,
				hub_id,
				current_password,
				password,
				password_confirmation,
			},
		});
	};

	signOut = async (): Promise<SignOutResponse> => {
		return await this.sendRequest({ method: 'DELETE', path: '/session' });
	};

	userMentions = async (
		query: string,
		hubId: string,
		limit: number = 5
	): Promise<UserMentionsResponse> => {
		const params = new URLSearchParams();
		params.append('q', query);
		params.append('limit', limit.toFixed());
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/users?${params.toString()}`,
		});
	};

	fetchUsers = async (
		hubId: string,
		roles: Array<'collaborator' | 'moderator' | 'owner' | 'member'>,
		query: string = '',
		limit: number = 30,
		page: number = 1,
		orderBy: 'name' = 'name'
	): Promise<Response> => {
		const params = new URLSearchParams();
		params.append('role', roles.join(','));
		params.append('q', query);
		params.append('limit', limit.toFixed());
		params.append('page', page.toFixed());
		params.append('order_by', orderBy);
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/users?${params.toString()}`,
		});
	};

	requestResetPassword = async (email: string, hubId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/password_reset_intent`,
			data: { email },
		});
	};

	fetchNotifications = async (
		hubId: string,
		options?: { showRead?: boolean; limit?: number; page?: number }
	): Promise<NotificationResponse> => {
		const params = new URLSearchParams();

		if (!!options?.showRead) {
			params.append('showRead', JSON.stringify(options.showRead));
		}

		if (typeof options?.limit === 'number' && options.limit > 0) {
			params.append('limit', JSON.stringify(options.limit));
		}

		if (typeof options?.page === 'number' && options.page > 0) {
			params.append('page', JSON.stringify(options.page));
		}

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/notifications?${params.toString()}`,
		});
	};

	dismissNotifications = async (hubId: string, lastUnreadId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/notifications`,
			data: {
				until_id: lastUnreadId,
			},
		});
	};

	reorderCollections = async (
		hubId: string,
		collectionIds: string[]
	): Promise<CollectionsResponse> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/collections/reorder`,
			data: collectionIds,
		});
	};

	/**
	 * @description Fetches collections for a specified hub
	 * @param {boolean} [includeArchived=false] - Whether to include archived collections, default is false
	 * @param {number} [limit=100] - Maximum number of collections to return, default is 100
	 */
	fetchCollections = async (
		hubId: string,
		includeArchived: boolean = false,
		limit: number = 100
	): Promise<CollectionsResponse> => {
		const params = new URLSearchParams();

		params.append('archived', includeArchived.toString());
		params.append('limit', limit.toString());

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/collections?${params.toString()}`,
		});
	};

	fetchCollection = async (hubId: string, collectionId: string): Promise<CollectionResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/collections/${collectionId}`,
		});
	};

	fetchSamlConfigList = async (hubId: string): Promise<Response<SamlConfig[]>> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/saml`,
		});
	};

	fetchSamlErrors = async (hubId: string, samlId: string): Promise<Response<SamlError[]>> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/saml/${samlId}/errors`,
		});
	};

	fetchSocialSignInMemberCount = async (
		hubId: string,
		provider: SocialSsoProvider
	): Promise<Response<number>> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/auth/${provider}/count`,
		});
	};

	fetchPostContributions = async (
		hubId: string,
		postId: string,
		rootId?: string,
		limit: number = 20,
		order: 'asc' | 'desc' = 'desc'
	): Promise<FetchPostContributions> => {
		const params = new URLSearchParams();
		if (rootId) {
			params.append('root_id', rootId);
		}
		params.append('limit', limit.toString());
		if (order) {
			params.append('order', order);
		}
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/posts/${postId}/contributions?${params.toString()}`,
		});
	};

	createCollection = async (
		hubId: string,
		collection: CreateCollectionRequest
	): Promise<CollectionResponse> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/collections`,
			data: collection,
		});
	};

	createContribution = async (hubId: string, id: string, post: PostParent): Promise<Response> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/posts/${id}/contributions`,
			data: {
				...post,
			},
		});
	};

	rejectSubmission = async (post: Post, reason: string) => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${post.hub_id}/posts/submissions/${post.id}/reject`,
			data: { reason },
		});
	};

	rejectSubmissions = async (hubId: string, rejectedPostsId: Array<string>, reason: string) => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/submissions/reject`,
			data: { postIDs: rejectedPostsId, reason },
		});
	};

	approveSubmission = async (post: Post | PostParent, hubId: string) => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/submissions/${post.id}/approve`,
		});
	};

	approveSubmissions = async (hubId: string, approvedPostsId: Array<string>, reason: string) => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/submissions/approve`,
			data: { postIDs: approvedPostsId, reason },
		});
	};

	publishDraft = async (post: Post | PostParent, hubId: string) => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/drafts/${post.id}/publish`,
			data: post,
		});
	};

	submitDraft = async (post: PostParent, hubId: string) => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/drafts/${post.id}/submit`,
			data: post,
		});
	};

	unSchedulePost = async (post: Post) => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${post.hub_id}/posts/scheduled/${post.id}/unpublish`,
		});
	};

	savePost = async (post: PostParent, hubId: string) => {
		const isNewPost = !post.id;

		let endpoint = null;
		switch (post.status) {
			case 'draft':
				endpoint = 'drafts';
				break;
			case 'in-review':
				endpoint = 'submissions';
				break;
			case 'scheduled':
				endpoint = 'scheduled';
				break;
			case 'hidden':
				endpoint = 'manage';
				break;
			default:
				endpoint = null;
		}

		let path = `/hubs/${hubId}/posts`;

		if (endpoint) {
			path += `/${endpoint}`;
		}

		if (!isNewPost) {
			path += `/${post.id}`;
		}

		return await this.sendRequest({
			method: isNewPost ? 'POST' : 'PUT',
			path,
			data: post,
		});
	};

	fetchPosts = async (hubId: string, searchParams?: URLSearchParams): Promise<PostsResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/posts${searchParams ? `?${searchParams.toString()}` : ''}`,
		});
	};

	updateCollection = async (
		hubId: string,
		collection: CustomCollection
	): Promise<CollectionResponse> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/collections/${collection.id}`,
			data: collection,
		});
	};

	updateContribution = async (
		hubId: string,
		postId: string,
		contributionId: string,
		post: PostParent
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/${postId}/contributions/${contributionId}`,
			data: {
				...post,
			},
		});
	};

	updateHub = async (hubId: string, hub: Hub): Promise<HubResponse> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}`,
			data: hub,
		});
	};

	updateUser = async (hubId: string, user: User): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/users/${user.id}`,
			data: {
				...user,
			},
		});
	};

	removeUser = async (hubId: string, userId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/hubs/${hubId}/users/${userId}`,
		});
	};

	impersonateUser = async (hubId: string, userId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/users/${userId}/impersonate`,
		});
	};
	//	https://techgolang.van.sit1.vcilabs.com/hub/_api/hubs/1729382256910271211/engagement/posts?limit=20&order_by=-total_views&start=2016-10-01T00%3A00%3A00.000Z&end=2022-03-22T22%3A59%3A59.999Z
	fetchPerformancePagePosts = async (
		hubId: string,
		limit: number = 20,
		order_by: 'total_views' | 'like_count' | 'comment_count' = 'total_views',
		from_highest?: boolean,
		start?: Date,
		end?: Date
	): Promise<FetchPerformancePagePostsResponse> => {
		const params = new URLSearchParams();
		params.append('limit', limit.toString());
		params.append('order_by', from_highest ? '-' + order_by : order_by);

		// Weird hack with 2016 date, that is how reference implementation works
		start
			? params.append('start', start.toISOString())
			: params.append('start', new Date(2016, 10, 1).toISOString());
		end ? params.append('end', end.toISOString()) : params.append('end', new Date().toISOString());

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/engagement/posts?${params.toString()}`,
		});
	};

	//	https://techgolang.van.sit1.vcilabs.com/hub/_api/hubs/1729382256910271211/engagement
	fetchPerformancePageEnagegement = async (
		hubId: string,
		start?: Date,
		end?: Date
	): Promise<FetchPerformancePageEnagegmentResponse> => {
		const params = new URLSearchParams();

		// Weird hack with 2016 date, that is how reference implementation works
		start
			? params.append('start', start.toISOString())
			: params.append('start', new Date(2016, 10, 1).toISOString());
		end ? params.append('end', end.toISOString()) : params.append('end', new Date().toISOString());

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/engagement?${params.toString()}`,
		});
	};

	//https://techgolang.van.sit1.vcilabs.com/hub/_api/hubs/1729382256910271211/posts/published/1729382256910527026/engagement/users?limit=20&order_by=-views
	fetchPerformancePageUsers = async (
		hubId: string,
		postId: string,
		limit: number = 20,
		order_by: 'views' | 'comments' | 'likes' = 'views',
		from_highest?: boolean,
		manage: boolean = false
	): Promise<Response> => {
		const params = new URLSearchParams();
		params.append('limit', limit.toString());
		params.append('order_by', from_highest ? '-' + order_by : order_by);

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/posts/${
				!manage ? 'published' : 'manage'
			}/${postId}/engagement/users?${params.toString()}`,
		});
	};

	//https://techgolang.van.sit1.vcilabs.com/hub/_api/hubs/1729382256910271211/posts/published/1729382256910527026/engagement?limit=20&order_by=-views
	fetchPerformancePageUsersEngagement = async (
		hubId: string,
		postId: string,
		manage: boolean = false
	): Promise<FetchPerformancePageEnagegmentResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/posts/${!manage ? 'published' : 'manage'}/${postId}/engagement/`,
		});
	};

	fetchPostManage = async (
		hubId: string,
		limit: number = 25,
		type?: string,
		collection?: string,
		query?: string,
		until?: number
	): Promise<FetchPostManage> => {
		const params = new URLSearchParams();

		if (limit) params.append('limit', limit.toString());
		if (type && type != 'all') params.append('type', type);
		if (collection) params.append('collection', collection);
		if (query) params.append('q', query);
		if (until) params.append('until', until.toString());

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/posts/manage?${params.toString()}`,
		});
	};

	fetchPostDraftScheduledSubmissions = async (
		hubId: string,
		limit: number = 25,
		endpoint: 'drafts' | 'scheduled' | 'submissions',
		options?: {
			type?: string;
			sentiment?: string;
			until?: number;
		}
	): Promise<FetchPostManage> => {
		const params = new URLSearchParams();

		if (limit) params.append('limit', limit.toString());
		if (endpoint === 'submissions' && options?.sentiment && options.sentiment != 'all')
			params.append('sentiment', options.sentiment);
		if (options?.type && options.type != 'all') params.append('type', options.type);
		if (options?.until) params.append('until', options.until.toString());

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/posts/${endpoint}?${params.toString()}`,
		});
	};

	updatePostManageOrder = async (
		hubId: string,
		postId: string,
		beforeId: string
	): Promise<{
		error?: any;
		status: number;
	}> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/manage/${postId}/reorder`,
			data: {
				id: postId,
				before: beforeId,
			},
		});
	};

	//https://techgolang.van.sit1.vcilabs.com/hub/_api/hubs/1729382256910271211/posts/published/1729382256910527026/engagement/users/1729382256911294434?event_type=VIEW%2C%20POST_COMMENT%2C%20POST_LIKE&limit=20
	fetchPerformancePageUser = async (
		hubId: string,
		postId: string,
		userId: string,
		limit: number = 20,
		event_type: Array<'VIEW' | 'POST_COMMENT' | 'POST_LIKE'> = [
			'VIEW',
			'POST_COMMENT',
			'POST_LIKE',
		],
		manage: boolean = false
	): Promise<FetchPerformancePageUserResponse> => {
		const params = new URLSearchParams();
		params.append('limit', limit.toString());
		params.append('order_by', event_type.join(','));

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/posts/${
				!manage ? 'published' : 'manage'
			}/${postId}/engagement/users/${userId}?${params.toString()}`,
		});
	};

	fetchActivitySettings = async (hubId: string): Promise<notificationSettingResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/subscriptions/contribution`,
		});
	};
	updateActivitySettings = async (hubId: string, postComments: boolean): Promise<Response> => {
		return await this.sendRequest({
			method: postComments ? 'POST' : 'DELETE',
			path: `/hubs/${hubId}/subscriptions/contribution`,
		});
	};
	fetchNewsLetterSettings = async (hubId: string): Promise<notificationSettingResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/subscriptions/newsletter`,
		});
	};
	updateNewsletterSettings = async (hubId: string, newsletter: boolean): Promise<Response> => {
		return await this.sendRequest({
			method: newsletter ? 'POST' : 'DELETE',
			path: `/hubs/${hubId}/subscriptions/newsletter`,
		});
	};

	inviteViaEmail = async (hubId: string, data: InviteByEmailData): Promise<Response> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/invites`,
			data,
		});
	};

	pinPost = async (hubId: string, postId: string, position: number): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/published/${postId}/lock`,
			data: { id: postId, position },
		});
	};

	unpinPost = async (hubId: string, postId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/published/${postId}/unlock`,
		});
	};

	hidePost = async (hubId: string, postId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/published/${postId}/hide`,
		});
	};

	unhidePost = async (hubId: string, postId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/posts/manage/${postId}/show`,
		});
	};

	deletePost = async (hubId: string, postId: string, postStatus: string): Promise<Response> => {
		if (postStatus === 'published' || postStatus === 'hidden') {
			return await this.sendRequest({
				method: 'DELETE',
				path: `/hubs/${hubId}/posts/manage/${postId}`,
			});
		} else if (postStatus === 'draft') {
			return await this.sendRequest({
				method: 'DELETE',
				path: `/hubs/${hubId}/posts/drafts/${postId}`,
			});
		} else if (postStatus === 'scheduled') {
			return await this.sendRequest({
				method: 'DELETE',
				path: `/hubs/${hubId}/posts/scheduled/${postId}`,
			});
		} else {
			return await this.sendRequest({
				method: 'DELETE',
				path: `/hubs/${hubId}/posts/submissions/${postId}`,
			});
		}
	};

	deleteCollection = async (hubId: string, collectionId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/hubs/${hubId}/collections/${collectionId}`,
		});
	};

	deleteContribution = async (
		hubId: string,
		postId: string,
		status: string,
		contirbutionId: string
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/hubs/${hubId}/posts/${status}/${postId}/contributions/${contirbutionId}`,
		});
	};

	deleteSamlConfig = async (hubId: string, samlId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/hubs/${hubId}/saml/${samlId}`,
		});
	};

	fetchTags = async (hubId: string, query = ''): Promise<FetchTagsResponse> => {
		//This endpoint do not respect the query param (according to the bluesuede implementation, it should).
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/tags?q=${query}`,
		});
	};

	/*
	Endpoint does not work now
	TODO: Fix response type to {data?, error?}
	fetchSimilarPosts = async (post: Post): Promise<Array<Post>> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${post.hub.id}/posts/${post.id}/similar`,
		});
	};
	*/

	createNewsletter = async (hubId: string): Promise<NewsletterResponse> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/newsletters/v2`,
		});
	};

	fetchNewsletter = async (hubId: string, newsletterId: string): Promise<NewsletterResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/newsletters/${newsletterId}`,
		});
	};

	fetchNewsletters = async (
		hubId: string,
		scope: Array<string>,
		start: Date = new Date(0),
		end: Date = new Date()
	): Promise<Response<Newsletter[]>> => {
		const params = new URLSearchParams();
		params.append('scope', scope.join(','));
		params.append('start', start.toISOString());
		params.append('end', end.toISOString());
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/newsletters?${params.toString()}`,
		});
	};

	fetchEmailMetrics = async <T extends string>(
		hubId: string,
		objectId: Array<string>,
		event: Array<string>,
		start: Date = new Date(0),
		end: Date = new Date(),
		groupBy: Array<T>,
		distinct: string = 'email',
		page: number = 1,
		limit: number = 1000
	): Promise<Omit<Response, 'data'> & { data?: Array<Record<T | 'id', string>> }> => {
		const params = new URLSearchParams();
		params.append('start', start.toISOString());
		params.append('end', end.toISOString());
		params.append('object_id', objectId.join(','));
		params.append('event', event.join(','));
		params.append('group_by', groupBy.join(','));
		params.append('limit', limit.toString());
		//params.append('distinct', distinct);
		params.append('page', page.toString());
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/metrics/email?${params.toString()}`,
		});
	};

	newsletterPreview = async (
		hubId: string,
		newsletterId: string,
		previewRequest: NewsletterPreviewRequest
	): Promise<NewsletterPreviewResponse> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/newsletters/${newsletterId}/preview/v2`,
			data: previewRequest,
		});
	};
	fetchNewsletterRecipients = async (
		hubId: string,
		newsletterId: string,
		page: Page,
		filter?: string
	): Promise<NewsletterRecipientsResponse> => {
		const params = new URLSearchParams();
		params.append('limit', page.size.toFixed());
		params.append('page', page.page.toFixed());
		if (filter) params.append('q', filter);
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/newsletters/${newsletterId}/recipients?${params.toString()}`,
		});
	};

	unscheduleNewsletter = async (
		hubId: string,
		newsletterId: string
	): Promise<NewsletterResponse> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/newsletters/${newsletterId}/unschedule`,
		});
	};
	fetchActivities = async (hubId: string): Promise<ActivitiesResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/sparq3/member/activities`,
		});
	};

	createWidget = async (hubId: string, widget: any): Promise<Response> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/widgets`,
			data: widget,
		});
	};

	exportAllComments = async (
		hubId: string,
		filters: CommentFilter,
		additionalContext: boolean = false
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/posts/export`,
			data: {
				hubId,
				filters,
				additionalContext,
			},
		});
	};

	fetchProfileVariablesMetaData = async (hubID: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubID}/profile-variables/metadata`,
		});
	};

	updateProfileVariablesMetaData = async (
		hubID: string,
		variables: Array<PV>
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubID}/profile-variables/metadata`,
			data: variables,
		});
	};

	updateProfileVariablesMetaDataOrder = async (
		hubID: string,
		variableID: string,
		order: number
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubID}/profile-variables/metadata/order/${variableID}`,
			data: {
				order,
			},
		});
	};

	fetchProfileVariableCategories = async (hubID: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubID}/profile-variables/categories`,
		});
	};

	updateProfileVariablesCategories = async (
		hubID: string,
		categoryName: string
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubID}/profile-variables/categories`,
			data: {
				name: categoryName,
			},
		});
	};

	removeProfileVariablesCategory = async (hubID: string, categoryID: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/hubs/${hubID}/profile-variables/categories/${categoryID}`,
		});
	};

	updateProfileVariablesCategory = async (
		hubID: string,
		category: PVCategory
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubID}/profile-variables/categories/${category.id}`,
			data: category,
		});
	};

	fetchAddons = async (hubId: string): Promise<AddonsResponse> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/addons`,
		});
	};

	updateSamlConfig = async (hubId: string, saml: SamlConfig): Promise<Response<SamlConfig>> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/saml/${saml.id}`,
			data: saml,
		});
	};

	updateConfig = async (hubId: string, config: ConfigsEntity): Promise<Response> => {
		return await this.sendRequest({
			method: 'PUT',
			path: `/hubs/${hubId}/addons/${config.id}`,
			data: {
				hub_id: hubId,
				is_enabled: config.is_enabled,
				addon_type: config.addon_type,
				label: config.label,
				values: config.values,
			},
		});
	};

	removeConfig = async (hubId: string, config: ConfigsEntity): Promise<Response> => {
		return await this.sendRequest({
			method: 'DELETE',
			path: `/hubs/${hubId}/addons/${config.id}`,
		});
	};

	createConfig = async (hubId: string, config: ConfigsEntity): Promise<Response> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/addons/`,
			data: config,
		});
	};

	createSamlConfig = async (
		hubId: string,
		provider: SamlProvider
	): Promise<Response<SamlConfig>> => {
		return await this.sendRequest({
			method: 'POST',
			path: `/hubs/${hubId}/saml`,
			data: { type: provider },
		});
	};
	fetchIndividualMemberGroup = async (
		hubId: string,
		memberId: string
	): Promise<Response<sparq3FilterPayload>> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/sparq3/filters/${memberId}`,
		});
	};
	fetchMemberGroup = async (hubId: string, query: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/sparq3/filters?${query}`,
		});
	};
	fetchSparqActiveMembers = async (hubId: string): Promise<Response> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/sparq3/filters/activemembers`,
		});
	};
	fetchSparqActivities = async (
		hubId: string,
		options: {
			limit: number;
			sort: string;
			activityTypes: string[];
		} = {
			limit: 50,
			sort: 'lastUpdateDate:d' as const,
			activityTypes: ['Sparq1Survey', 'Survey', 'Forum'] as const,
		}
	): Promise<Response<sparq3FilterPayload[]>> => {
		const params = new URLSearchParams();

		const { limit, sort, activityTypes } = options;

		params.append('limit', limit.toString());
		params.append('sort', sort);

		const typeQuery = activityTypes.map((type) => `activityType="${type}"`).join(' OR ');
		params.append('q', typeQuery);

		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/sparq3/activities?${params.toString()}`,
		});
	};
	fetchSparqActivityStatus = async (
		hubId: string,
		activityId: string,
		status: string
	): Promise<Response> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/sparq3/activities/${activityId}?status=${status}`,
		});
	};
	fetchNextActivity = async (hubId: string, searchParams?: URLSearchParams): Promise<Response> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/hubs/${hubId}/sparq3/activities${searchParams ? `?${searchParams.toString()}` : ''}`,
		});
	};

	fetchAIConsent = async (accountId: string, hubUID: string): Promise<Response<AIConsent>> => {
		return await this.sendRequest({
			method: 'GET',
			path: `/organizations/${accountId}/hubs/${hubUID}/aiconsent`,
		});
	};
}
